import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, Calendar, CalendarX, Clock, MapPin, Video } from 'lucide-react';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import CoachAvatar from '../components/booking/CoachAvatar';
import { PublicCardShell, PublicLogo } from '../components/public/PublicCardShell';
import { joinButtonLabel, locationLabel } from '../components/schedule/locationOptions';
import {
    REASON_MAX,
    THROTTLED_COPY,
    GENERIC_CANCEL_ERROR,
    cancelErrorCopy,
    canCancel,
    hostFirstName,
    hostName,
    hostNameInline,
    loadErrorState,
    locationText,
    manageState,
    meetingName,
    safeHttpUrl,
    settleCancelResponse,
    whenLines,
    whenSummary,
} from '../lib/bookingManage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_BRAND = '#1c1917';
const DISPLAY_FONT = { fontFamily: 'Manrope, sans-serif' };

// The guest's private page for ONE booking (#791): `/book/manage/:token`. The
// token is the capability; there is no auth and the requests carry no auth
// header. It is server-driven (E32): every state comes from `view.state` and
// `view.can_cancel`, and the only client-only fact is "this tab's POST just
// cancelled it". No server `detail` is ever rendered; the copy is fixed per
// status (lib/bookingManage.js).

// What the one live region says on each state change. A region mounted WITH its
// state announces nothing, so it stays mounted outside the state switch.
const ANNOUNCEMENTS = {
    cancelled_now: 'Your booking is cancelled.',
    cancelled_guest: 'Your booking is cancelled.',
    cancelled_host: 'This booking was cancelled.',
    started: 'This meeting has already started.',
    ended: 'This meeting has ended.',
    invalid: "This link isn't valid.",
    throttled: THROTTLED_COPY,
    failed: "We couldn't load your booking.",
};

const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2';
const buttonBase = `inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl px-5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60 ${focusRing}`;
const outlineButton = `${buttonBase} border border-stone-300 bg-white text-stone-800 hover:bg-stone-50`;
const destructiveButton = `${buttonBase} bg-[#A3382B] hover:bg-[#8A2E23] text-white`;
const cardClass = 'bg-white border border-stone-200 rounded-2xl shadow-sm';

function brandColorOf(view) {
    const color = view?.link?.color;
    return typeof color === 'string' && /^#[0-9a-f]{3,8}$/i.test(color.trim()) ? color.trim() : DEFAULT_BRAND;
}

// The page's one <h1> for a state. Focus moves here on every state change, so
// it is programmatically focusable and draws no ring of its own.
const Heading = forwardRef(function Heading({ children, className = '' }, ref) {
    return (
        <h1 ref={ref} tabIndex={-1} style={DISPLAY_FONT}
            className={`text-stone-900 outline-none ${className}`}>
            {children}
        </h1>
    );
});

function LinkButton({ href, brandColor, children, testId, newTab = false }) {
    return (
        <a href={href} rel="noopener noreferrer" {...(newTab ? { target: '_blank' } : {})}
            className={`${buttonBase} mt-5 text-white`} style={{ backgroundColor: brandColor }}
            data-testid={testId}>
            {children}
        </a>
    );
}

function DetailRow({ icon: Icon, children }) {
    return (
        <div className="flex items-start gap-3">
            <Icon className="mt-0.5 h-4 w-4 flex-none text-stone-500" aria-hidden="true" />
            <div className="min-w-0 flex-1 text-sm text-stone-600">{children}</div>
        </div>
    );
}

function DetailsCard({ view, brandColor }) {
    const when = whenLines(view);
    const where = locationText(view, locationLabel);
    const joinHref = safeHttpUrl(view.meet_link);
    return (
        <div className={`${cardClass} p-5`} data-testid="manage-details">
            <CoachAvatar size="md" photoUrl={view.host?.photo_url} name={hostName(view)} title={view.host?.title} />
            <div className="my-4 border-t border-stone-200" />
            <div className="space-y-3">
                {when.date && (
                    <DetailRow icon={Calendar}><p className="font-medium text-stone-800">{when.date}</p></DetailRow>
                )}
                {(when.time || when.zone) && (
                    <DetailRow icon={Clock}>
                        {when.time && <p className="font-medium text-stone-800">{when.time}</p>}
                        {when.zone && <p className="text-xs text-stone-500">{when.zone}</p>}
                    </DetailRow>
                )}
                {where && <DetailRow icon={MapPin}><p>{where}</p></DetailRow>}
            </div>
            {joinHref && (
                <LinkButton href={joinHref} newTab brandColor={brandColor} testId="manage-join">
                    <Video className="h-4 w-4" aria-hidden="true" />
                    {/* #755 / E33: a pasted static link can be any provider, so it gets the neutral label. */}
                    {joinButtonLabel(view.meeting_link_source === 'static' ? null : view.location)}
                </LinkButton>
            )}
            <p className="mt-4 text-sm font-semibold text-stone-900" style={DISPLAY_FONT} data-testid="manage-meeting-name">
                {meetingName(view)}
            </p>
        </div>
    );
}

// A card that carries the whole state: icon, h1, one or two lines, one action.
function MessageCard({ icon: Icon, headingRef, title, children, action }) {
    return (
        <div className={`${cardClass} p-8 text-center`}>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-stone-100">
                <Icon className="h-6 w-6 text-stone-500" aria-hidden="true" />
            </div>
            <Heading ref={headingRef} className="text-lg font-semibold">{title}</Heading>
            {children}
            {action}
        </div>
    );
}

function ManageLoading() {
    return (
        <div className={`${cardClass} p-5`} aria-busy="true" data-testid="manage-loading">
            <div className="flex items-center gap-3">
                <div className="h-10 w-10 flex-none animate-pulse rounded-full bg-stone-200" />
                <div className="flex-1 space-y-2">
                    <div className="h-3 w-1/2 animate-pulse rounded bg-stone-200" />
                    <div className="h-3 w-1/3 animate-pulse rounded bg-stone-200" />
                </div>
            </div>
            <div className="mt-5 space-y-3">
                <div className="h-3 w-3/4 animate-pulse rounded bg-stone-200" />
                <div className="h-3 w-2/3 animate-pulse rounded bg-stone-200" />
            </div>
            <p role="status" className="mt-5 text-center text-sm text-stone-600">Loading your booking…</p>
        </div>
    );
}

export default function PublicBookingManage() {
    const { token } = useParams();

    // loading | ready | invalid | throttled | failed
    const [phase, setPhase] = useState('loading');
    const [attempt, setAttempt] = useState(0);
    const [view, setView] = useState(null);
    const [justCancelled, setJustCancelled] = useState(false);

    const [expanded, setExpanded] = useState(false);
    const [reason, setReason] = useState('');
    const [pending, setPending] = useState(false);
    const [cancelError, setCancelError] = useState('');

    const headingRef = useRef(null);
    const openerRef = useRef(null);
    const reasonRef = useRef(null);
    const confirmRef = useRef(null);
    const didExpandRef = useRef(false);

    // 1) Load the booking. A token that fails the format check is the server's
    // 404, so there is no client-side guess about it.
    useEffect(() => {
        let alive = true;
        setPhase('loading');
        (async () => {
            try {
                const res = await axios.get(`${API}/booking/public/manage/${encodeURIComponent(token)}`);
                if (!alive) return;
                if (!res.data || typeof res.data !== 'object') { setPhase('failed'); return; }
                setView(res.data);
                setPhase('ready');
            } catch (err) {
                if (!alive) return;
                setPhase(loadErrorState(err));
            }
        })();
        return () => { alive = false; };
    }, [token, attempt]);

    const uxState = phase === 'ready' ? manageState(view, { justCancelled }) : phase;

    // Focus follows the state: to the <h1> when loading ends and on every change
    // after, so a screen-reader user lands on the new state's heading.
    useEffect(() => {
        if (uxState === 'loading') return;
        headingRef.current?.focus();
    }, [uxState]);

    // Opening the cancel form focuses the textarea; closing it returns to the
    // button that opened it. The textarea itself is never remounted while typing.
    useEffect(() => {
        if (expanded) {
            didExpandRef.current = true;
            reasonRef.current?.focus();
        } else if (didExpandRef.current) {
            didExpandRef.current = false;
            openerRef.current?.focus();
        }
    }, [expanded]);

    // A failed cancel leaves the buttons live again; put focus on the one that retries.
    useEffect(() => {
        if (cancelError) confirmRef.current?.focus();
    }, [cancelError]);

    const confirmCancel = async () => {
        if (pending) return;
        setPending(true);
        setCancelError('');
        const trimmed = reason.trim();
        try {
            const res = await axios.post(
                `${API}/booking/public/manage/${encodeURIComponent(token)}/cancel`,
                trimmed ? { reason: trimmed } : {},
            );
            const settled = settleCancelResponse(res.data, view);
            if (!settled) {
                setCancelError(GENERIC_CANCEL_ERROR);
                return;
            }
            setView(settled.view);
            setJustCancelled(settled.justCancelled);
            setExpanded(false);
        } catch (err) {
            const copy = cancelErrorCopy(err);
            if (copy.state === 'started') {
                // The 409 carries the view, so no refetch.
                setView(copy.view || { ...view, state: 'started', can_cancel: false });
                setExpanded(false);
            } else if (copy.state === 'invalid') {
                setPhase('invalid');
                setExpanded(false);
            } else {
                setCancelError(copy.message);
            }
        } finally {
            setPending(false);
        }
    };

    const keepBooking = () => {
        setExpanded(false);
        setReason('');
        setCancelError('');
    };

    const announcement = pending ? 'Cancelling your booking…' : cancelError || ANNOUNCEMENTS[uxState] || '';
    const brandColor = brandColorOf(phase === 'ready' ? view : null);
    const rebookHref = phase === 'ready' ? safeHttpUrl(view?.link?.rebook_url) : '';
    // A deleted link has no branding to show (E32): no logo, the stone default colour, no rebook.
    const logoSrc = phase === 'ready' && view?.link ? view.org?.logo_url : null;

    const retry = (
        <button type="button" onClick={() => setAttempt((n) => n + 1)}
            className={`${buttonBase} mt-5 bg-stone-900 text-white hover:bg-stone-800`} data-testid="manage-retry">
            Try again
        </button>
    );
    const rebook = rebookHref ? (
        <LinkButton href={rebookHref} brandColor={brandColor} testId="manage-rebook">Book another time</LinkButton>
    ) : null;

    let content = null;
    if (uxState === 'loading') {
        content = <ManageLoading />;
    } else if (uxState === 'invalid') {
        content = (
            <MessageCard icon={AlertCircle} headingRef={headingRef} title="This link isn't valid">
                <p className="mt-2 text-sm text-stone-600">
                    It may have been copied incompletely. Use the link in your confirmation email, or contact the person you booked with.
                </p>
            </MessageCard>
        );
    } else if (uxState === 'throttled') {
        content = (
            <MessageCard icon={Clock} headingRef={headingRef} title="Please wait a moment" action={retry}>
                <p className="mt-2 text-sm text-stone-600">{THROTTLED_COPY}</p>
            </MessageCard>
        );
    } else if (uxState === 'failed') {
        content = (
            <MessageCard icon={AlertCircle} headingRef={headingRef} title="Something went wrong" action={retry}>
                <p className="mt-2 text-sm text-stone-600">We couldn't load your booking.</p>
            </MessageCard>
        );
    } else if (uxState === 'cancelled_host') {
        content = (
            <MessageCard icon={CalendarX} headingRef={headingRef} title="This booking was cancelled" action={rebook}>
                <p className="mt-2 text-sm text-stone-600">{hostName(view)} cancelled this meeting.</p>
            </MessageCard>
        );
    } else if (uxState === 'cancelled_now' || uxState === 'cancelled_guest') {
        const first = hostFirstName(view);
        content = (
            <MessageCard icon={CalendarX} headingRef={headingRef} title="Your booking is cancelled" action={rebook}>
                <p className="mt-2 text-sm text-stone-600" data-testid="manage-cancelled-when">
                    {[whenSummary(view), `with ${hostNameInline(view)}`].filter(Boolean).join(' ')}
                </p>
                {uxState === 'cancelled_now' && (
                    <p className="mt-1 text-sm text-stone-600" data-testid="manage-host-told">
                        {`We've let ${first || 'your host'} know.`}
                    </p>
                )}
            </MessageCard>
        );
    } else {
        // upcoming | started | ended: the details card, and the action card while the server allows a cancel.
        const first = hostFirstName(view);
        const showCancel = canCancel(view);
        const title = uxState === 'started' ? 'This meeting has already started'
            : uxState === 'ended' ? 'This meeting has ended' : 'Your booking';
        content = (
            <>
                <Heading ref={headingRef} className="mb-4 text-2xl font-bold">{title}</Heading>
                <DetailsCard view={view} brandColor={brandColor} />
                {showCancel ? (
                    <div className={`${cardClass} mt-4 p-5`} data-testid="manage-actions">
                        {!expanded ? (
                            <>
                                <h2 className="text-base font-semibold text-stone-900" style={DISPLAY_FONT}>Can't make it?</h2>
                                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                                    <button ref={openerRef} type="button" onClick={() => setExpanded(true)}
                                        className={outlineButton} data-testid="manage-open-cancel">
                                        Cancel booking
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h2 className="text-base font-semibold text-stone-900" style={DISPLAY_FONT}>Cancel this booking?</h2>
                                <p className="mt-1 text-sm text-stone-600">
                                    {`${first || 'Your host'} will be told, and the time will be freed for others.`}
                                </p>
                                <Label htmlFor="manage-cancel-reason" className="mt-4 block text-stone-800">
                                    Reason (optional)
                                </Label>
                                <Textarea
                                    id="manage-cancel-reason"
                                    ref={reasonRef}
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    maxLength={REASON_MAX}
                                    rows={3}
                                    readOnly={pending}
                                    aria-describedby="manage-cancel-reason-count"
                                    className="mt-2 resize-none border-stone-300 bg-white text-stone-900 focus-visible:ring-2 focus-visible:ring-stone-900"
                                    data-testid="manage-reason"
                                />
                                <p id="manage-cancel-reason-count" className="mt-1 text-right text-xs text-stone-500"
                                    data-testid="manage-reason-count">
                                    {reason.length} / {REASON_MAX}
                                </p>
                                {cancelError && (
                                    <p role="alert" className="mt-3 text-sm text-[#A3382B]" data-testid="manage-cancel-error">
                                        {cancelError}
                                    </p>
                                )}
                                <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                                    <button type="button" onClick={keepBooking} disabled={pending}
                                        className={`${outlineButton} sm:flex-1`} data-testid="manage-keep">
                                        Keep booking
                                    </button>
                                    <button ref={confirmRef} type="button" onClick={confirmCancel} disabled={pending}
                                        className={`${destructiveButton} sm:flex-1`} data-testid="manage-confirm-cancel">
                                        {pending ? 'Cancelling…' : 'Cancel booking'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <p className="mt-4 text-sm text-stone-600" data-testid="manage-reply-note">
                        To change anything, reply to your confirmation email.
                    </p>
                )}
            </>
        );
    }

    return (
        <PublicCardShell footer>
            {/* Always mounted, outside the state switch. */}
            <div aria-live="polite" aria-atomic="true" className="sr-only" data-testid="manage-live-region">
                {announcement}
            </div>
            <PublicLogo src={logoSrc} />
            {/* Keyed by state so each state's heading is a fresh node that takes focus. */}
            <div key={uxState}>{content}</div>
        </PublicCardShell>
    );
}
